import React, { useState, useRef, useEffect } from "react";
import { Search, Plus, X, Image, Play } from "lucide-react";

export const ExerciseAutocomplete = ({
    allExercises = [],
    onSelect,
    onAddNew,
    placeholder = "Search exercise...",
    value = "",
    className = "",
    colors,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        // Only sync if value changes and we are not currently searching/editing
        // This prevents overrides while user is typing if parent components re-render
        if (!isOpen && value !== searchTerm) {
            setSearchTerm(value);
        } else if (value && !searchTerm) {
            // Initial load
            setSearchTerm(value);
        }
    }, [value, isOpen, searchTerm]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target)
            ) {
                setIsOpen(false);
                // Reset search term to value if unchanged
                if (value) setSearchTerm(value);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [value]);

    // Deduplicate exercises to prevent duplicates in the list
    const uniqueExercises = React.useMemo(() => {
        const seen = new Set();
        return allExercises.filter(ex => {
            const id = ex.id || ex.SK;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [allExercises]);

    const filteredExercises = uniqueExercises
        .filter((ex) => {
            const name = ex.name || "";
            const category = ex.category || "";
            const term = searchTerm.toLowerCase().trim();
            if (!term) return true;
            return name.toLowerCase().includes(term) || category.toLowerCase().includes(term);
        })
        .slice(0, 50);

    const listRef = useRef(null);

    useEffect(() => {
        setHighlightedIndex(-1);
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [searchTerm]);

    // ... (rest of component)

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === "ArrowDown" || e.key === "Enter") {
                setIsOpen(true);
            }
            return;
        }

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    prev < filteredExercises.length - 1 ? prev + 1 : 0
                );
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                break;
            case "Enter":
                e.preventDefault();
                if (filteredExercises[highlightedIndex]) {
                    handleSelect(filteredExercises[highlightedIndex]);
                } else if (searchTerm.trim() && onAddNew) {
                    handleAddNew(searchTerm.trim());
                }
                break;
            case "Escape":
                setIsOpen(false);
                setHighlightedIndex(0);
                break;
            case "Tab":
                if (isOpen && filteredExercises[highlightedIndex]) {
                    e.preventDefault(); // Prevent standard tab behavior if list is open
                    handleSelect(filteredExercises[highlightedIndex]);
                } else {
                    setIsOpen(false);
                }
                break;
            default:
                break;
        }
    };

    const handleSelect = (exercise) => {
        onSelect(exercise);
        setSearchTerm(exercise.name);
        setIsOpen(false);
        setHighlightedIndex(0);
    };

    const handleAddNew = (name) => {
        if (onAddNew) {
            onAddNew(name);
            setIsOpen(false);
        }
    }

    // Check if URL is a video
    const isVideoUrl = (url) => {
        if (!url) return false;
        const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
        const lowerUrl = url.toLowerCase();
        return (
            videoExtensions.some((ext) => lowerUrl.includes(ext)) ||
            lowerUrl.includes("youtube") ||
            lowerUrl.includes("vimeo") ||
            lowerUrl.includes("video")
        );
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${colors.textMuted}`} />
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        setHighlightedIndex(0);
                    }}
                    onFocus={() => {
                        setIsOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className={`w-full pl-9 pr-8 py-2 text-sm rounded-lg border ${colors.inputBorder || 'border-gray-200'} ${colors.inputBg || 'bg-white'} ${colors.inputText || 'text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all shadow-sm`}
                />
                {searchTerm && (
                    <button
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
                        onClick={(e) => {
                            e.stopPropagation(); // prevent closing
                            setSearchTerm("");
                            onSelect({ id: null, name: "" }); // Optional: clear selection in parent if needed, but risky if not handled
                            setIsOpen(true);
                            inputRef.current?.focus();
                        }}
                    >
                        <X className={`w-3 h-3 ${colors.textMuted}`} />
                    </button>
                )}
            </div>

            {isOpen && (
                <div
                    ref={listRef}
                    className={`absolute z-50 w-full mt-1 ${colors.bgSecondary || 'bg-white'} rounded-xl shadow-xl border ${colors.border || 'border-gray-200'} max-h-80 overflow-y-auto overflow-x-hidden`}
                >
                    {filteredExercises.length === 0 ? (
                        <div className="p-3">
                            <p className={`text-sm ${colors.textMuted} text-center py-2`}>No exercises found</p>
                            {onAddNew && searchTerm.trim() && (
                                <button
                                    onClick={() => handleAddNew(searchTerm.trim())}
                                    className="w-full mt-2 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-xs font-medium"
                                >
                                    <Plus className="w-3 h-3" />
                                    <span>Create "{searchTerm}"</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="py-1">
                            {filteredExercises.map((ex, index) => {
                                const hasMedia = ex.mediaUrls && ex.mediaUrls.length > 0;
                                const mediaUrl = hasMedia ? ex.mediaUrls[0] : null;
                                const isVideo = isVideoUrl(mediaUrl);

                                return (
                                    <div
                                        key={ex.id || index}
                                        onClick={() => handleSelect(ex)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        className={`flex items-center px-3 py-2 cursor-pointer transition-colors ${index === highlightedIndex
                                            ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                            : `${colors.textPrimary} hover:bg-gray-50 dark:hover:bg-gray-800`
                                            }`}
                                    >
                                        {/* Tiny Media Preview */}
                                        <div className="w-8 h-8 rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 mr-3 border border-gray-200 dark:border-gray-700">
                                            {hasMedia ? (
                                                isVideo ? (
                                                    <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                                        <Play className="w-3 h-3 text-white" />
                                                    </div>
                                                ) : (
                                                    <img src={mediaUrl} alt="" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                                                )
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <Image className="w-3 h-3 text-gray-400" />
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium truncate">{ex.name}</div>
                                            {ex.category && (
                                                <div className={`text-xs ${colors.textMuted || 'text-gray-500'} truncate`}>{ex.category}</div>
                                            )}
                                        </div>

                                        {ex.difficulty && (
                                            <span className={`text-[10px] px-1.5 py-0.5 rounded ml-2 font-medium
                             ${ex.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
                                                    ex.difficulty === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                                        'bg-red-100 text-red-700'
                                                }
                           `}>
                                                {ex.difficulty.substring(0, 3)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Add New Option at the bottom as well */}
                            {onAddNew && searchTerm.trim() && (
                                <div className={`border-t ${colors.border || 'border-gray-200'} mt-1 pt-1 px-1 pb-1`}>
                                    <button
                                        onClick={() => handleAddNew(searchTerm.trim())}
                                        className="w-full flex items-center justify-center space-x-2 px-3 py-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors text-xs font-medium"
                                    >
                                        <Plus className="w-3 h-3" />
                                        <span>Create "{searchTerm}"</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
